import { useEffect, useMemo, useRef, useState } from "react";
import { MARKET_API_BASE, MARKET_WS_BASE, marketFetch, openMarketSocket } from "../backendConfig";
import { useAuth } from "../auth/AuthContext";
import { fonts, palette, pageStyles } from "./pageStyles";

const DEPTH_LEVELS = 12;
const HEATMAP_COLUMNS = 60;
const RANGE_MS = {
  "15m": 15 * 60_000,
  "1h": 60 * 60_000,
  "4h": 4 * 60 * 60_000,
  "24h": 24 * 60 * 60_000,
  "7d": 7 * 24 * 60 * 60_000,
};

const panel = {
  background: palette.glass,
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  backdropFilter: "blur(12px)",
  minWidth: 0,
};
const compactButton = {
  minHeight: 34,
  padding: "0 11px",
  borderRadius: 5,
  border: `1px solid ${palette.borderStrong}`,
  background: "rgba(255,255,255,.035)",
  color: palette.text,
  font: `500 12px ${fonts.ui}`,
  cursor: "pointer",
};
const field = {
  minHeight: 36,
  padding: "0 10px",
  borderRadius: 5,
  border: `1px solid ${palette.borderStrong}`,
  background: palette.field,
  color: palette.text,
  font: `500 12px ${fonts.code}`,
};

function fmtPrice(value) {
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function fmtTime(ms) {
  return ms ? new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
}

function DepthHeatmap({ snapshots }) {
  const frames = snapshots.slice(-HEATMAP_COLUMNS);
  const prices = useMemo(() => {
    const last = frames[frames.length - 1];
    if (!last) return [];
    const candidates = [
      ...(last.asks || []).slice(0, DEPTH_LEVELS).map((level) => Number(level.price)),
      ...(last.bids || []).slice(0, DEPTH_LEVELS).map((level) => Number(level.price)),
    ];
    return [...new Set(candidates.filter(Number.isFinite))].sort((a, b) => b - a);
  }, [frames]);
  const bookMaps = useMemo(() => frames.map((frame) => {
    const map = new Map();
    (frame.bids || []).forEach((level) => map.set(Number(level.price), { size: Number(level.size) || 0, side: "bid" }));
    (frame.asks || []).forEach((level) => map.set(Number(level.price), { size: Number(level.size) || 0, side: "ask" }));
    return map;
  }), [frames]);
  const maxSize = Math.max(0, ...bookMaps.flatMap((map) => [...map.values()].map((entry) => entry.size)));

  if (!frames.length || !prices.length) {
    return <div style={{ color: palette.mutedText, fontSize: 12, padding: "22px 14px" }}>Depth heatmap will appear when the provider sends Level II updates.</div>;
  }
  return (
    <div style={{ overflow: "auto", maxHeight: "min(42vh, 440px)", padding: "10px 12px" }}>
      <div role="grid" aria-label="Recorded market depth heatmap" style={{ minWidth: 540 }}>
        <div role="row" style={{ display: "grid", gridTemplateColumns: `88px repeat(${frames.length}, minmax(7px, 1fr))`, gap: 2, alignItems: "center", marginBottom: 4 }}>
          <span style={{ color: palette.dimText, font: `10px ${fonts.code}` }}>PRICE</span>
          {frames.map((frame, i) => <span key={`${frame.time}-${i}`} title={new Date(frame.time).toLocaleString()} style={{ height: 14, color: palette.dimText, textAlign: "center", font: `9px ${fonts.code}`, overflow: "hidden" }}>{i === 0 || i === frames.length - 1 || i % Math.max(1, Math.floor(frames.length / 6)) === 0 ? fmtTime(frame.time).slice(0, 5) : ""}</span>)}
        </div>
        {prices.map((price) => (
          <div role="row" key={price} style={{ display: "grid", gridTemplateColumns: `88px repeat(${frames.length}, minmax(7px, 1fr))`, gap: 2, alignItems: "center", height: 18 }}>
            <span style={{ color: palette.mutedText, font: `10px ${fonts.code}`, overflow: "hidden" }}>{fmtPrice(price)}</span>
            {bookMaps.map((map, i) => {
              const level = map.get(price);
              const strength = level && maxSize ? Math.max(0.12, Math.sqrt(level.size / maxSize)) : 0;
              const color = level?.side === "bid" ? "95,224,168" : "255,125,140";
              return <span key={`${price}-${i}`} title={level ? `${fmtTime(frames[i].time)} · ${level.side} ${fmtPrice(price)} · ${level.size.toLocaleString()} units` : undefined} style={{ height: 15, borderRadius: 2, background: strength ? `rgba(${color},${0.12 + 0.78 * strength})` : "rgba(255,255,255,.025)" }} />;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function DepthLadder({ snapshot }) {
  const bids = (snapshot?.bids || []).slice(0, DEPTH_LEVELS);
  const asks = (snapshot?.asks || []).slice(0, DEPTH_LEVELS);
  const maxSize = Math.max(1, ...bids.map((x) => Number(x.size) || 0), ...asks.map((x) => Number(x.size) || 0));
  const rows = [
    ...asks.slice().reverse().map((level) => ({ ...level, side: "ask" })),
    ...bids.map((level) => ({ ...level, side: "bid" })),
  ];
  if (!rows.length) return <div style={{ padding: 18, color: palette.mutedText, fontSize: 12 }}>Waiting for provider depth levels…</div>;
  return (
    <div style={{ overflow: "auto", maxHeight: 520 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", font: `12px ${fonts.code}` }}>
        <thead><tr style={{ color: palette.dimText, textAlign: "right", fontSize: 10 }}><th style={{ padding: "8px 10px", textAlign: "left" }}>SIDE</th><th style={{ padding: 8 }}>PRICE</th><th style={{ padding: 8 }}>SIZE (UNITS)</th><th style={{ padding: "8px 12px 8px 4px", width: "35%" }}>DEPTH</th></tr></thead>
        <tbody>{rows.map((row, index) => {
          const size = Number(row.size) || 0;
          const color = row.side === "bid" ? palette.up : palette.down;
          return (
            <tr key={`${row.side}-${row.price}-${index}`} style={{ borderTop: `1px solid ${palette.border}`, color: row.side === "bid" ? palette.up : palette.down }}>
              <td style={{ padding: "5px 10px", textAlign: "left", opacity: 0.86 }}>{row.side.toUpperCase()}</td>
              <td style={{ padding: 5, textAlign: "right", fontWeight: 650 }}>{fmtPrice(row.price)}</td>
              <td style={{ padding: 5, textAlign: "right", color: palette.text }}>{size.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
              <td style={{ padding: "5px 12px 5px 4px" }}><div style={{ height: 8, borderRadius: 2, background: "rgba(255,255,255,.05)", overflow: "hidden" }}><div style={{ height: "100%", width: `${Math.min(100, size / maxSize * 100)}%`, marginLeft: row.side === "ask" ? "auto" : 0, background: color, opacity: 0.72 }} /></div></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

export default function MarketDepthPage() {
  const { token } = useAuth();
  const [symbol, setSymbol] = useState("EURUSD");
  const [draftSymbol, setDraftSymbol] = useState("EURUSD");
  const [marketInfo, setMarketInfo] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [frames, setFrames] = useState([]);
  const [status, setStatus] = useState("Checking feed…");
  const [notice, setNotice] = useState("");
  const [historyRange, setHistoryRange] = useState("1h");
  const [replayRows, setReplayRows] = useState([]);
  const [replayIndex, setReplayIndex] = useState(0);
  const [replaying, setReplaying] = useState(false);
  const [loadingReplay, setLoadingReplay] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    let stopped = false;
    let retryTimer = null;
    let retryCount = 0;
    let socket = null;
    const connect = async () => {
      setStatus("Checking provider…");
      try {
        const response = await marketFetch(`${MARKET_API_BASE}/market/info`);
        const info = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(info?.detail || `Feed status failed (${response.status})`);
        if (stopped) return;
        setMarketInfo(info);
        if (!info?.capabilities?.market_depth || info?.is_simulated) {
          setStatus("Depth unavailable");
          setNotice("The configured data provider does not expose market depth. No sample or synthetic book is shown.");
          return;
        }
        setStatus("Connecting to depth feed…");
        setNotice("");
        socket = openMarketSocket(`${MARKET_WS_BASE}/depth/stream?symbol=${encodeURIComponent(symbol)}`);
        socketRef.current = socket;
        socket.onopen = () => { retryCount = 0; setStatus("Waiting for depth updates…"); };
        socket.onmessage = (event) => {
          let data;
          try { data = JSON.parse(event.data); } catch { return; }
          if (data.type === "error") {
            setNotice(data.message || "Market depth is unavailable for this symbol.");
            setStatus("Depth unavailable");
            return;
          }
          setSnapshot(data);
          if (data.status === "live") setStatus("Live depth");
          else if (data.status === "quiet") setStatus("Feed connected · quiet");
          else if (data.status === "reconnecting") setStatus("Reconnecting to provider…");
          else setStatus("Waiting for provider depth…");
          setFrames((previous) => [...previous.slice(-(HEATMAP_COLUMNS * 3 - 1)), data]);
        };
        socket.onerror = () => setStatus((previous) => previous === "Live depth" ? previous : "Reconnecting…");
        socket.onclose = (event) => {
          socketRef.current = null;
          if (stopped) return;
          if ([1008, 4401].includes(event.code)) {
            setStatus(event.code === 4401 ? "Sign in required" : "Depth unavailable");
            return;
          }
          retryCount += 1;
          const delay = Math.min(15_000, 700 * (2 ** Math.min(retryCount, 5)));
          setStatus("Reconnecting…");
          retryTimer = setTimeout(connect, delay);
        };
      } catch (error) {
        if (stopped) return;
        setStatus("Feed connection failed");
        setNotice(error.message || "Could not check the market provider.");
      }
    };
    setFrames([]);
    setSnapshot(null);
    setReplayRows([]);
    setReplaying(false);
    connect();
    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      socket?.close();
      socketRef.current = null;
    };
  }, [symbol, token]);

  useEffect(() => {
    if (!replaying || replayRows.length < 2) return undefined;
    const timer = setInterval(() => setReplayIndex((index) => {
      if (index >= replayRows.length - 1) {
        setReplaying(false);
        return index;
      }
      return index + 1;
    }), 500);
    return () => clearInterval(timer);
  }, [replaying, replayRows.length]);

  const displayed = replaying || replayRows.length ? (replayRows[replayIndex] || snapshot) : snapshot;
  const historyFrames = useMemo(() => {
    if (replayRows.length) return replayRows.slice(Math.max(0, replayIndex - HEATMAP_COLUMNS + 1), replayIndex + 1);
    return frames.slice(-HEATMAP_COLUMNS);
  }, [frames, replayRows, replayIndex]);
  const bestBid = displayed?.bids?.[0]?.price;
  const bestAsk = displayed?.asks?.[0]?.price;
  const spread = Number.isFinite(bestBid) && Number.isFinite(bestAsk) ? bestAsk - bestBid : null;

  const loadReplay = async () => {
    setLoadingReplay(true);
    setNotice("");
    setReplaying(false);
    const end = Date.now();
    const start = end - (RANGE_MS[historyRange] || RANGE_MS["1h"]);
    try {
      const response = await marketFetch(`${MARKET_API_BASE}/depth/history?symbol=${encodeURIComponent(symbol)}&from=${start}&to=${end}&limit=1500`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.detail || `History request failed (${response.status})`);
      const rows = Array.isArray(body.snapshots) ? body.snapshots : [];
      setReplayRows(rows);
      setReplayIndex(0);
      if (rows.length) {
        setReplaying(true);
        setNotice(`Replaying ${rows.length.toLocaleString()} recorded snapshots. Depth history begins when the terminal first records the provider feed.`);
      } else {
        setNotice("No recorded provider depth in this time range yet. History is recorded while a live depth page is connected.");
      }
    } catch (error) {
      setNotice(error.message || "Could not load depth history.");
    } finally {
      setLoadingReplay(false);
    }
  };

  const useLive = () => {
    setReplaying(false);
    setReplayRows([]);
  };
  const progress = replayRows.length > 1 ? replayIndex / (replayRows.length - 1) : 0;

  return (
    <div style={{ ...pageStyles.page, height: "100%", minHeight: 0, overflow: "auto", padding: "14px clamp(12px, 2vw, 24px) 24px", boxSizing: "border-box", gap: 10 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "3px 2px" }}>
        <div style={{ minWidth: 170, marginRight: "auto" }}>
          <div style={{ color: palette.dimText, font: `10px ${fonts.code}`, letterSpacing: ".12em" }}>LEVEL II WORKSPACE</div>
          <h1 style={{ margin: "4px 0 0", font: `500 19px ${fonts.display}`, letterSpacing: "-.025em" }}>Market Depth</h1>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 7, color: palette.mutedText, fontSize: 11 }}>
          Symbol
          <input value={draftSymbol} onChange={(event) => setDraftSymbol(event.target.value.toUpperCase())} onKeyDown={(event) => { if (event.key === "Enter" && draftSymbol.trim()) setSymbol(draftSymbol.trim().toUpperCase()); }} aria-label="Depth symbol" style={{ ...field, width: 120 }} />
        </label>
        <button type="button" style={compactButton} onClick={() => draftSymbol.trim() && setSymbol(draftSymbol.trim().toUpperCase())}>Load symbol</button>
        <span role="status" style={{ color: status === "Live depth" ? palette.up : status.includes("unavailable") ? palette.down : palette.mutedText, fontSize: 11, minWidth: 120 }}>{status}</span>
      </header>

      {notice && <div role="status" style={{ ...panel, padding: "9px 12px", color: palette.mutedText, fontSize: 11, lineHeight: 1.5 }}>{notice}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
        {[
          ["Provider", marketInfo?.provider || "—"],
          ["Mode", marketInfo?.data_mode || "—"],
          ["Best bid", Number.isFinite(bestBid) ? fmtPrice(bestBid) : "—"],
          ["Best ask", Number.isFinite(bestAsk) ? fmtPrice(bestAsk) : "—"],
          ["Spread", spread == null ? "—" : fmtPrice(spread)],
          ["Observed (local)", fmtTime(displayed?.time)],
        ].map(([label, value]) => <div key={label} style={{ ...panel, padding: "9px 12px" }}><div style={{ color: palette.dimText, font: `10px ${fonts.code}`, textTransform: "uppercase" }}>{label}</div><div style={{ color: palette.text, marginTop: 5, font: `500 13px ${fonts.code}`, overflowWrap: "anywhere" }}>{value}</div></div>)}
      </div>

      <section style={{ ...panel, overflow: "hidden" }} aria-label="Live market depth ladder">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: `1px solid ${palette.border}` }}>
          <div><div style={{ fontSize: 12, fontWeight: 600 }}>Depth ladder · {symbol}</div><div style={{ color: palette.dimText, fontSize: 10, marginTop: 3 }}>Broker aggregated MBP · size in provider units</div></div>
          <div style={{ color: palette.mutedText, fontSize: 10, textAlign: "right" }}>{displayed?.classification || "Waiting for provider classification"}</div>
        </div>
        <DepthLadder snapshot={displayed} />
      </section>

      <section style={{ ...panel, overflow: "hidden" }} aria-label="Market depth heatmap">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px 0" }}>
          <div><div style={{ fontSize: 12, fontWeight: 600 }}>Liquidity heatmap</div><div style={{ color: palette.dimText, fontSize: 10, marginTop: 3 }}>Price levels across recorded time · green bids · red asks</div></div>
          <div role="group" aria-label="Depth replay controls" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
            <select aria-label="Replay period" value={historyRange} onChange={(event) => setHistoryRange(event.target.value)} style={{ ...field, minHeight: 34 }}>
              {Object.keys(RANGE_MS).map((range) => <option value={range} key={range}>{range}</option>)}
            </select>
            <button type="button" style={compactButton} onClick={loadReplay} disabled={loadingReplay}>{loadingReplay ? "Loading…" : "Replay recorded depth"}</button>
            {replayRows.length > 0 && <>
              <button type="button" style={compactButton} onClick={() => setReplaying((playing) => !playing)}>{replaying ? "Pause" : "Play"}</button>
              <button type="button" style={compactButton} onClick={() => { setReplaying(false); setReplayIndex((index) => Math.min(replayRows.length - 1, index + 1)); }}>Step +1</button>
              <button type="button" style={compactButton} onClick={useLive}>Live</button>
            </>}
          </div>
        </div>
        {replayRows.length > 1 && <div style={{ display: "grid", gridTemplateColumns: "minmax(100px,1fr) auto", alignItems: "center", gap: 10, padding: "7px 12px 0" }}><input aria-label="Depth replay position" type="range" min="0" max={replayRows.length - 1} value={replayIndex} onChange={(event) => { setReplaying(false); setReplayIndex(Number(event.target.value)); }} style={{ width: "100%", accentColor: palette.highlight }} /><span style={{ color: palette.mutedText, font: `10px ${fonts.code}` }}>{fmtTime(replayRows[replayIndex]?.time)} · {replayIndex + 1}/{replayRows.length}</span></div>}
        <DepthHeatmap snapshots={historyFrames} />
        {replayRows.length > 1 && <div aria-hidden="true" style={{ height: 2, background: `linear-gradient(90deg,${palette.highlight} ${progress * 100}%,rgba(255,255,255,.06) ${progress * 100}%)` }} />}
      </section>

      <footer style={{ color: palette.dimText, fontSize: 10, lineHeight: 1.5, padding: "0 2px" }}>
        {marketInfo?.notes?.find((note) => /depth/i.test(note)) || "Displayed depth is broker-provided and aggregated by price. It is not represented as a consolidated exchange book. The terminal does not infer individual orders."}
        {" "}Historical replay contains only snapshots recorded while a signed-in user had the live depth workspace connected.
      </footer>
    </div>
  );
}
